import React from 'react';
import SearchIcon from '../../Images/search-icon.svg';
import DownloadIcon from '../../Images/download-icon.svg';
import { CustomerLite } from '../Plants/CreatePlant';

const DropdownChevron = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

type Props = {
    searchText: string;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    customers: CustomerLite[];
    selectedCustomerId: string;
    isCustomerOpen: boolean;
    customerSearch: string;
    filteredCustomersList: CustomerLite[];
    onCustomerOpenToggle: () => void;
    onCustomerSearchChange: (value: string) => void;
    onCustomerSelect: (customerId: string) => void;
    selectedUserType: string;
    isUserTypeOpen: boolean;
    userTypeSearch: string;
    filteredUserTypesList: string[];
    onUserTypeOpenToggle: () => void;
    onUserTypeSearchChange: (value: string) => void;
    onUserTypeSelect: (userType: string) => void;
    onDownload: () => void;
    onAddUser: () => void;
    btnDisabled: boolean;
};

function getSelectedCustomerLabel(customers: CustomerLite[], selectedCustomerId: string): string {
    if (!selectedCustomerId) {
        return '';
    }
    return customers.find(c => c.customerId === selectedCustomerId)?.customerName || '';
}

const ProfileFilters: React.FC<Props> = ({
    searchText,
    onSearchChange,
    customers,
    selectedCustomerId,
    isCustomerOpen,
    customerSearch,
    filteredCustomersList,
    onCustomerOpenToggle,
    onCustomerSearchChange,
    onCustomerSelect,
    selectedUserType,
    isUserTypeOpen,
    userTypeSearch,
    filteredUserTypesList,
    onUserTypeOpenToggle,
    onUserTypeSearchChange,
    onUserTypeSelect,
    onDownload,
    onAddUser,
    btnDisabled,
}) => {
    const selectedCustomerLabel = getSelectedCustomerLabel(customers, selectedCustomerId);
    const customerInputValue = isCustomerOpen ? customerSearch : selectedCustomerLabel;
    const customerPlaceholder = isCustomerOpen
        ? 'Search customers...'
        : (selectedCustomerLabel || 'All Customers');
    const userTypeInputValue = isUserTypeOpen ? userTypeSearch : (selectedUserType || '');
    const userTypePlaceholder = isUserTypeOpen
        ? 'Search user types...'
        : (selectedUserType || 'User Type: All');

    const openDropdownOnArrowKey = (e: React.KeyboardEvent<HTMLInputElement>, open: () => void) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            open();
        }
    };

    return (
        <div className="table-header">
            <div className="header-actions">
                <div className="search-container-plant">
                    <img className='search-icon-lg-user' src={SearchIcon} alt="search" />
                    <input
                        type="text"
                        placeholder="Search"
                        value={searchText}
                        onChange={onSearchChange}
                        className="search-input-user user-placeholder"
                    />
                    <div className="searchable-select-containers">
                        <div className="searchable-select-input" onClick={onCustomerOpenToggle}>
                            <input
                                type="text"
                                value={customerInputValue}
                                onChange={(e) => onCustomerSearchChange(e.target.value)}
                                onKeyDown={(e) => openDropdownOnArrowKey(e, onCustomerOpenToggle)}
                                placeholder={customerPlaceholder}
                                className="user-cust searchable-input"
                                readOnly={!isCustomerOpen}
                                style={{ height: '36px', borderRadius: '4px' }}
                            />
                            <span className={`dropdown-arrow profile-arrow ${isCustomerOpen ? 'open' : ''}`}>
                                <DropdownChevron />
                            </span>
                        </div>
                        {isCustomerOpen && (
                            <div className="profile-searchable-dropdown">
                                <div
                                    className={`dropdown-option ${selectedCustomerId === '' ? 'selected' : ''}`}
                                    onClick={() => onCustomerSelect('')}
                                >
                                    All Customers
                                </div>
                                {filteredCustomersList.map((customer, index) => {
                                    const label = `${customer.customerName}${customer.soldTo ? ` (${customer.soldTo})` : ''}`;
                                    return (
                                        <div
                                            key={customer.id || `${customer.customerId}-${index}`}
                                            className={`dropdown-option ${customer.customerId === selectedCustomerId ? 'selected' : ''}`}
                                            onClick={() => onCustomerSelect(customer.customerId)}
                                        >
                                            {label}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    &nbsp;&nbsp;
                    <div className="searchable-select-containers">
                        <div className="searchable-select-input" onClick={onUserTypeOpenToggle}>
                            <input
                                type="text"
                                value={userTypeInputValue}
                                onChange={(e) => onUserTypeSearchChange(e.target.value)}
                                onKeyDown={(e) => openDropdownOnArrowKey(e, onUserTypeOpenToggle)}
                                placeholder={userTypePlaceholder}
                                className="user-cust searchable-input"
                                readOnly={!isUserTypeOpen}
                                style={{ height: '36px', borderRadius: '4px' }}
                            />
                            <span className={`dropdown-arrow profile-arrow ${isUserTypeOpen ? 'open' : ''}`}>
                                <DropdownChevron />
                            </span>
                        </div>
                        {isUserTypeOpen && (
                            <div className="usertype-dropdown">
                                <div
                                    className={`dropdown-option ${selectedUserType === 'All' ? 'selected' : ''}`}
                                    onClick={() => onUserTypeSelect('All')}
                                >
                                    User Type: All
                                </div>
                                {filteredUserTypesList.map((type, index) => (
                                    <div
                                        key={`${type}-${index}`}
                                        className={`dropdown-option ${type === selectedUserType ? 'selected' : ''}`}
                                        onClick={() => onUserTypeSelect(type)}
                                    >
                                        {type}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <button type="button" className="download-button-user" onClick={onDownload}>
                    <img className="download-icon-lg-user" src={DownloadIcon} alt="download" />
                    Download
                </button>
                <button type="button" className="create-user-button" onClick={onAddUser} disabled={btnDisabled}>
                    +&nbsp;&nbsp; Add User
                </button>
            </div>
        </div>
    );
};

export default ProfileFilters;
